/**
 * LogPickerModal.js
 *
 * Slide-up picker modal for selecting what to log.
 * Used by the simplified 4-button action bar on mobile.
 *
 * Options: Position, Weather, Fuel, Note, Arrive
 */

import React from 'react';
import { X, MapPin, Cloud, Fuel, FileText, Anchor, Compass, ClipboardCheck, CheckCircle } from 'lucide-react';
import { useDailyChecks } from '../../../../../hooks/domain/operations/useDailyChecks';

const LogPickerModal = ({
  isOpen,
  onClose,
  onSelectPosition,
  onSelectWeather,
  onSelectFuel,
  onSelectNote,
  onSelectCompass,
  onSelectArrive,
  onSelectDailyChecks,
  hasActiveOperation = false,
  preArrivalDone = false,  // FT-285 Item 4: hard-lock Arrive once pre_arrival operation_complete exists
  dailyChecksEnabled = false  // FT-80: Only show Daily Checks if enabled in Voyage Settings
}) => {
  const { getTodaySummary } = useDailyChecks();
  const summary = getTodaySummary();
  const allDailyChecksComplete = summary.completed === summary.total;

  if (!isOpen) return null;

  const handleSelect = (callback) => {
    onClose();
    callback?.();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Slide-up Panel */}
      <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
        <div className="bg-white rounded-t-2xl shadow-xl border-t border-slate-200 max-w-lg mx-auto safe-area-bottom">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Log Entry</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Options Grid */}
          <div className="p-4 grid grid-cols-3 gap-3">
            {/* Position */}
            <button
              onClick={() => handleSelect(onSelectPosition)}
              className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Position</span>
            </button>

            {/* Weather */}
            <button
              onClick={() => handleSelect(onSelectWeather)}
              className="flex flex-col items-center gap-2 p-4 bg-cyan-50 hover:bg-cyan-100 rounded-xl transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                <Cloud className="h-6 w-6 text-cyan-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Weather</span>
            </button>

            {/* Fuel */}
            <button
              onClick={() => handleSelect(onSelectFuel)}
              className="flex flex-col items-center gap-2 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Fuel className="h-6 w-6 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Fuel</span>
            </button>

            {/* Note */}
            <button
              onClick={() => handleSelect(onSelectNote)}
              className="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                <FileText className="h-6 w-6 text-slate-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Note</span>
            </button>

            {/* Compass */}
            <button
              onClick={() => handleSelect(onSelectCompass)}
              className="flex flex-col items-center gap-2 p-4 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Compass className="h-6 w-6 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Compass</span>
            </button>

            {/* Arrive / Mooring
                FT-285 Item 4: also locked when pre_arrival operation_complete
                already exists (preArrivalDone). Prevents duplicate state
                transition audit rows. */}
            {(() => {
              const arriveLocked = hasActiveOperation || preArrivalDone;
              return (
                <button
                  onClick={() => handleSelect(onSelectArrive)}
                  disabled={arriveLocked}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                    arriveLocked
                      ? 'bg-slate-50 opacity-50 cursor-not-allowed'
                      : 'bg-emerald-50 hover:bg-emerald-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    arriveLocked ? 'bg-slate-200' : 'bg-emerald-100'
                  }`}>
                    <Anchor className={`h-6 w-6 ${arriveLocked ? 'text-slate-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className={`text-sm font-medium ${arriveLocked ? 'text-slate-400' : 'text-slate-700'}`}>
                    Arrive
                  </span>
                </button>
              );
            })()}

            {/* Daily Checks - FT-80: Only show if enabled in Voyage Settings */}
            {dailyChecksEnabled && (
              <button
                onClick={() => handleSelect(onSelectDailyChecks)}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${
                  allDailyChecksComplete
                    ? 'bg-green-50 hover:bg-green-100'
                    : 'bg-purple-50 hover:bg-purple-100'
                }`}
              >
                <div className={`relative w-12 h-12 rounded-full flex items-center justify-center ${
                  allDailyChecksComplete ? 'bg-green-100' : 'bg-purple-100'
                }`}>
                  {allDailyChecksComplete ? (
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  ) : (
                    <ClipboardCheck className="h-6 w-6 text-purple-600" />
                  )}
                  {/* Progress badge */}
                  {!allDailyChecksComplete && summary.completed > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {summary.completed}
                    </span>
                  )}
                </div>
                <span className={`text-sm font-medium ${allDailyChecksComplete ? 'text-green-700' : 'text-slate-700'}`}>
                  Checks {summary.completed}/{summary.total}
                </span>
              </button>
            )}
          </div>

          {/* Safe area spacing for iOS */}
          <div className="h-6"></div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default LogPickerModal;
